/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';
import store from 'store';

// Weirdly this interface isn't exported upstream, but we can get at it indirectly.
type StoreJSApi = typeof store;

function get(storeBackend: StoreJSApi, key: string) {
  try {
    return storeBackend.get(key);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to retrieve initialize state from localStorage:', e);
  }
}

function set(storeBackend: StoreJSApi, key: string, value: object) {
  try {
    storeBackend.set(key, value);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to persist state from localStorage:', e);
  }
}

/**
 * @description
 * Creates a redux store enhancer that syncs (a subset of) store state to localStorage. The
 * configuration object allows multiple store backends to be involved, with one backend tracking
 * some keys and different backend tracking other keys. It is up to the caller to make sure that no
 * single store key is tracked by multiple backends
 * @param {Array} backendConfigs An array of configs. Each config includes a store backend and the keys
 *   it is responsible for managing.
 * @param {boolean | undefined} throttle True if store updates should be throttled
 *
 * @returns {Function} An enhanced store
 */
export function createComplexLocalStorageEnhancer(
  backendConfigs: {
    storeBackend: StoreJSApi;
    storeKeys: string[];
  }[],
  throttle?: boolean
) {
  return (next: any) => (reducer: any, initialState: any, enhancer: any) => {
    const enabledBackends = backendConfigs.filter(({ storeBackend }) => storeBackend.enabled);
    if (enabledBackends.length === 0) {
      return next(reducer, initialState, enhancer);
    }

    let finalInitialState = {
      ...initialState,
    };
    enabledBackends.forEach(({ storeBackend }) => {
      finalInitialState = {
        ...finalInitialState,
        ...(get(storeBackend, 'redux') || {}),
      };
    });

    const store = next(reducer, finalInitialState, enhancer);

    const storeUpdater = () => {
      const state = store.getState();
      enabledBackends.forEach(({ storeBackend, storeKeys }) => {
        const toBeSaved: any = {};
        storeKeys.forEach((key) => {
          toBeSaved[key] = state[key];
        });
        set(storeBackend, 'redux', toBeSaved);
      });
    };

    const maybeThrottledStoreUpdater = throttle
      ? _.throttle(storeUpdater, 1000, {
          leading: true,
          trailing: true,
        })
      : storeUpdater;

    store.subscribe(maybeThrottledStoreUpdater);

    return store;
  };
}

/**
 * @description
 * Creates a redux store enhancer that syncs (a subset of) store state to localStorage.
 * @param {Array} stores List of stores to sync.
 * @param {Object} storeBackend A storage engine of store.js.
 * @param {boolean | undefined} throttle True if store updates should be throttled
 *
 * @returns {Function} An enhanced store
 */
export function createLocalStorageEnhancer(
  stores: string[],
  storeBackend: StoreJSApi,
  throttle?: boolean
) {
  return createComplexLocalStorageEnhancer(
    [
      {
        storeBackend,
        storeKeys: stores,
      },
    ],
    throttle
  );
}
